import React, { useState, useMemo } from 'react'
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component'
import { IconTrash, IconPlus, IconX, IconCalendarEvent, IconCalendar, IconBuildingCommunity } from '@tabler/icons-react'
import ActionDropdown from '../../components/common/ActionDropdown'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import { useHolidays, useCreateHolidays, useDeleteHoliday, useCreateHolidayRange } from '../../hooks/useTimesheets'
import { useGoogleHolidays } from '../../hooks/useGoogleHolidays'
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal'

const TIME_OFF_TYPES = [
    { value: 'public_holiday', label: 'Public Holiday' },
    { value: 'company_holiday', label: 'Company Holiday' },
    { value: 'personal', label: 'Personal' },
    { value: 'sick', label: 'Sick' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'comp_time', label: 'Comp Time / TOIL' },
    { value: 'wfh', label: 'Work From Home' },
    { value: 'other', label: 'Other' },
]

const TABS = ['Single Date', 'Bulk', 'Date Range']

const currentYear = new Date().getFullYear()

export default function Holiday() {
    const { user } = useAuth()
    const orgId = user?.orgId
    const isHR = user?.is_superuser || user?.roles?.some(r => r.name?.toLowerCase().includes('hr')) || user?.role?.toLowerCase().includes('hr')

    const { data: backendHolidays = [], isLoading: loadingBackend } = useHolidays(orgId)
    const { data: googleHolidays = [], isLoading: loadingGoogle } = useGoogleHolidays(currentYear)
    const createHolidays = useCreateHolidays()
    const createHolidayRange = useCreateHolidayRange()
    const deleteHoliday = useDeleteHoliday()

    // Merge: google holidays as base
    const mergedHolidays = useMemo(() => {
        const googleList = (googleHolidays || []).map(h => ({
            ...h,
            source: 'google',
            type: 'public_holiday',
        }))
        const backendList = (Array.isArray(backendHolidays) ? backendHolidays : backendHolidays?.results || [])
            .map(h => ({
                id: h.id,
                name: h.name,
                date: h.date,
                type: h.time_off_type || 'company_holiday',
                source: 'custom',
            }))
        const googleDates = new Set(googleList.map(h => h.date))
        const filteredBackend = backendList.filter(h => !googleDates.has(h.date))
        return [...googleList, ...filteredBackend].sort((a, b) => a.date.localeCompare(b.date))
    }, [googleHolidays, backendHolidays])

    // Modal state
    const [modalOpen, setModalOpen] = useState(false)
    const [activeTab, setActiveTab] = useState(0)

    // Single form
    const [singleForm, setSingleForm] = useState({ name: '', date: '', time_off_type: 'public_holiday' })

    // Bulk form
    const [bulkRows, setBulkRows] = useState([{ name: '', date: '', time_off_type: 'public_holiday' }])

    // Range form
    const [rangeForm, setRangeForm] = useState({ name: '', start_date: '', end_date: '', skip_weekends: true })

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [itemToDelete, setItemToDelete] = useState(null)

    // Search
    const [filterText, setFilterText] = useState('')
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

    const openModal = () => {
        setModalOpen(true)
        setActiveTab(0)
        setSingleForm({ name: '', date: '', time_off_type: 'public_holiday' })
        setBulkRows([{ name: '', date: '', time_off_type: 'public_holiday' }])
        setRangeForm({ name: '', start_date: '', end_date: '', skip_weekends: true })
    }

    const closeModal = () => setModalOpen(false)

    const handleSingleSubmit = (e) => {
        e.preventDefault()
        if (!singleForm.name || !singleForm.date) {
            toast.error('Name and date are required')
            return
        }
        createHolidays.mutate(
            {
                data: {
                    scope: 'org',
                    date: singleForm.date,
                    name: singleForm.name,
                    time_off_type: singleForm.time_off_type,
                },
                orgId,
            },
            {
                onSuccess: () => {
                    toast.success(`Holiday "${singleForm.name}" created`)
                    closeModal()
                },
                onError: (err) => toast.error(err.message || 'Failed to create holiday'),
            }
        )
    }

    const handleBulkSubmit = (e) => {
        e.preventDefault()
        const valid = bulkRows.filter(r => r.name && r.date)
        if (valid.length === 0) {
            toast.error('Add at least one holiday with a name and date')
            return
        }
        createHolidays.mutate(
            {
                data: {
                    scope: 'org',
                    holidays: valid,
                },
                orgId,
            },
            {
                onSuccess: () => {
                    toast.success(`${valid.length} holiday(s) created`)
                    closeModal()
                },
                onError: (err) => toast.error(err.message || 'Failed to create holidays'),
            }
        )
    }

    const handleRangeSubmit = (e) => {
        e.preventDefault()
        if (!rangeForm.name || !rangeForm.start_date || !rangeForm.end_date) {
            toast.error('Name, start date and end date are required')
            return
        }
        createHolidayRange.mutate(
            {
                data: {
                    name: rangeForm.name,
                    start_date: rangeForm.start_date,
                    end_date: rangeForm.end_date,
                    skip_weekends: rangeForm.skip_weekends,
                },
                orgId,
            },
            {
                onSuccess: () => {
                    toast.success(`Holiday range "${rangeForm.name}" created`)
                    closeModal()
                },
                onError: (err) => toast.error(err.message || 'Failed to create holiday range'),
            }
        )
    }

    const handleDelete = (row) => {
        setItemToDelete(row)
        setDeleteModalOpen(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return
        try {
            await deleteHoliday.mutateAsync({ id: itemToDelete.id, orgId })
            toast.success(`"${itemToDelete.name}" deleted`)
            setDeleteModalOpen(false)
            setItemToDelete(null)
        } catch (err) {
            toast.error(err.message || 'Failed to delete holiday')
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return ''
        const d = new Date(dateStr + 'T00:00:00')
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    const getDayName = (dateStr) => {
        if (!dateStr) return ''
        const d = new Date(dateStr + 'T00:00:00')
        return d.toLocaleDateString('en-GB', { weekday: 'long' })
    }

    const filteredItems = mergedHolidays.filter(h =>
        h.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        h.date?.includes(filterText)
    )

    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle)
                setFilterText('')
            }
        }
        return (
            <div className='flex gap-5 mb-10 w-full justify-end'>
                <div className='relative w-full max-w-[221px]'>
                    <input
                        type='text'
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        placeholder='Search holidays...'
                        className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                    />
                </div>
                <button onClick={handleClear} className="btn btn-danger">Clear</button>
            </div>
        )
    }, [filterText, resetPaginationToggle])

    const conditionalRowStyles = [
        {
            when: row => row.source === 'custom',
            style: {
                borderLeft: '3px solid #6366f1',
                color: '#ffffff',
            },
        },
        {
            when: row => row.source === 'google',
            style: {
                borderLeft: '3px solid #9ca3af',
            },
        },
    ]

    const columns = [
        {
            name: 'NAME',
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <div className='flex items-center gap-8'>
                    <span>{row.name}</span>
                    {row.source === 'custom' && (
                        <span className='text-[10px] font-semibold px-[6px] py-[2px] rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'>
                            Custom
                        </span>
                    )}
                </div>
            ),
        },
        {
            name: 'DATE',
            selector: row => row.date,
            sortable: true,
            cell: row => formatDate(row.date),
        },
        {
            name: 'DAY',
            selector: row => row.date,
            cell: row => getDayName(row.date),
        },
        {
            name: 'TYPE',
            selector: row => row.type,
            cell: row => {
                const label = TIME_OFF_TYPES.find(t => t.value === row.type)?.label || row.type
                return <span className='text-sm'>{label}</span>
            },
        },
        {
            name: 'SOURCE',
            selector: row => row.source,
            cell: row => (
                <span className={`text-[11px] font-medium px-[8px] py-[3px] rounded-full ${
                    row.source === 'google'
                        ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                }`}>
                    {row.source === 'google' ? 'Public' : 'HR Created'}
                </span>
            ),
            width: '130px',
        },
        ...(isHR ? [{
            name: 'ACTION',
            cell: row => row.source === 'custom' ? (
                <ActionDropdown
                    menuItems={[
                        {
                            label: 'Delete',
                            icon: IconTrash,
                            danger: true,
                            description: 'Remove this holiday',
                            onClick: () => handleDelete(row),
                        },
                    ]}
                />
            ) : null,
            width: '80px',
            center: true,
        }] : []),
    ]

    const isLoading = loadingBackend || loadingGoogle
    const publicCount = mergedHolidays.filter(h => h.source === 'google').length
    const customCount = mergedHolidays.filter(h => h.source === 'custom').length

    return (
        <>
            <Breadcrumb title="Holidays" path="Holidays" />

            {/* Stats cards */}
            <div className='grid grid-cols-3 gap-15 mb-20'>
                <div className='bg-bg-dark rounded-lg shadow-light p-15 flex items-center gap-12'>
                    <div className='w-42 h-42 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0'>
                        <IconCalendarEvent size={20} />
                    </div>
                    <div>
                        <p className='text-[10px] font-bold uppercase tracking-widest text-text-secondary'>Total Holidays</p>
                        <p className='text-xl font-bold text-text-primary'>{mergedHolidays.length}</p>
                    </div>
                </div>
                <div className='bg-bg-dark rounded-lg shadow-light p-15 flex items-center gap-12'>
                    <div className='w-42 h-42 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0'>
                        <IconCalendar size={20} />
                    </div>
                    <div>
                        <p className='text-[10px] font-bold uppercase tracking-widest text-text-secondary'>Public Holidays</p>
                        <p className='text-xl font-bold text-text-primary'>{publicCount}</p>
                    </div>
                </div>
                <div className='bg-bg-dark rounded-lg shadow-light p-15 flex items-center gap-12'>
                    <div className='w-42 h-42 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0'>
                        <IconBuildingCommunity size={20} />
                    </div>
                    <div>
                        <p className='text-[10px] font-bold uppercase tracking-widest text-text-secondary'>HR Created</p>
                        <p className='text-xl font-bold text-text-primary'>{customCount}</p>
                    </div>
                </div>
            </div>

            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                <div className='flex items-center justify-between gap-15 mb-20'>
                    <div>
                        <p className='text-[16px]/[19.2px] font-medium'>Holidays</p>
                        <p className='text-xs text-text-secondary mt-1'>
                            Public holidays from calendar &amp; HR-created custom holidays
                        </p>
                    </div>
                    {isHR && (
                        <button onClick={openModal} className='btn btn-primary flex items-center gap-6'>
                            <IconPlus size={16} />
                            Add Holiday
                        </button>
                    )}
                </div>

                {/* Legend */}
                <div className='flex items-center gap-15 mb-15 text-xs text-text-secondary'>
                    <div className='flex items-center gap-6'>
                        <div className='w-8 h-8 rounded-full bg-gray-400'></div>
                        <span>Public (auto-loaded)</span>
                    </div>
                    <div className='flex items-center gap-6'>
                        <div className='w-8 h-8 rounded-full bg-indigo-500'></div>
                        <span>Custom (HR created)</span>
                    </div>
                </div>

                <div className='react-data-table'>
                    <ReactDataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponentMemo}
                        conditionalRowStyles={conditionalRowStyles}
                        progressPending={isLoading}
                        progressComponent={<div className='py-20 text-text-secondary text-sm'>Loading holidays...</div>}
                        noDataComponent={<div className='py-20 text-text-secondary text-sm'>No holidays found</div>}
                    />
                </div>
            </div>

            {/* Add Holiday Modal */}
            {modalOpen && (
                <>
                    <div className='fixed p-15 w-full max-w-[560px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                        <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                            <div className='flex items-center justify-between md:px-10 px-[7px] py-10 mb-0 border-b border-border'>
                                <h5 className='text-[16px]/[19.2px] font-medium'>Add Holiday</h5>
                                <button onClick={closeModal} className='text-text-secondary hover:text-text-primary'>
                                    <IconX size={20} />
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className='flex gap-0 border-b border-border md:px-10 px-[7px]'>
                                {TABS.map((tab, i) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(i)}
                                        className={`px-15 py-10 text-sm font-medium border-b-2 transition-colors ${
                                            activeTab === i
                                                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                                : 'border-transparent text-text-secondary hover:text-text-primary'
                                        }`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>

                            <div className='my-10 md:px-10 px-[7px] max-h-[60vh] overflow-auto'>

                                {/* Tab 0: Single Date */}
                                {activeTab === 0 && (
                                    <form onSubmit={handleSingleSubmit}>
                                        <div className='grid grid-cols-12 gap-15 mt-10'>
                                            <div className='form-group col-span-12'>
                                                <label className='text-sm mb-2 block'>Holiday Name *</label>
                                                <input
                                                    type='text'
                                                    className='w-full'
                                                    value={singleForm.name}
                                                    onChange={e => setSingleForm({ ...singleForm, name: e.target.value })}
                                                    placeholder='e.g. Christmas Day'
                                                />
                                            </div>
                                            <div className='form-group col-span-12'>
                                                <label className='text-sm mb-2 block'>Date *</label>
                                                <input
                                                    type='date'
                                                    className='w-full'
                                                    value={singleForm.date}
                                                    onChange={e => setSingleForm({ ...singleForm, date: e.target.value })}
                                                />
                                            </div>
                                            <div className='form-group col-span-12'>
                                                <label className='text-sm mb-2 block'>Type</label>
                                                <select
                                                    className='w-full'
                                                    value={singleForm.time_off_type}
                                                    onChange={e => setSingleForm({ ...singleForm, time_off_type: e.target.value })}
                                                >
                                                    {TIME_OFF_TYPES.map(t => (
                                                        <option key={t.value} value={t.value}>{t.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className='flex gap-10 justify-end mt-20'>
                                            <button type='button' onClick={closeModal} className='btn btn-outline-secondary'>Cancel</button>
                                            <button type='submit' className='btn btn-primary' disabled={createHolidays.isPending}>
                                                {createHolidays.isPending ? 'Saving...' : 'Save Holiday'}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* Tab 1: Bulk */}
                                {activeTab === 1 && (
                                    <form onSubmit={handleBulkSubmit}>
                                        <div className='mt-10 space-y-10'>
                                            {bulkRows.map((row, i) => (
                                                <div key={i} className='flex gap-10 items-center'>
                                                    <div className='form-group flex-1'>
                                                        {i === 0 && <label className='text-sm mb-2 block'>Name</label>}
                                                        <input
                                                            type='text'
                                                            className='w-full'
                                                            value={row.name}
                                                            onChange={e => {
                                                                const updated = [...bulkRows]
                                                                updated[i] = { ...updated[i], name: e.target.value }
                                                                setBulkRows(updated)
                                                            }}
                                                            placeholder='Holiday name'
                                                        />
                                                    </div>
                                                    <div className='form-group w-[160px]'>
                                                        {i === 0 && <label className='text-sm mb-2 block'>Date</label>}
                                                        <input
                                                            type='date'
                                                            className='w-full'
                                                            value={row.date}
                                                            onChange={e => {
                                                                const updated = [...bulkRows]
                                                                updated[i] = { ...updated[i], date: e.target.value }
                                                                setBulkRows(updated)
                                                            }}
                                                        />
                                                    </div>
                                                    <button
                                                        type='button'
                                                        onClick={() => setBulkRows(bulkRows.filter((_, idx) => idx !== i))}
                                                        className={`text-danger hover:opacity-70 ${i === 0 ? 'mt-[22px]' : ''}`}
                                                        disabled={bulkRows.length === 1}
                                                    >
                                                        <IconX size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type='button'
                                                onClick={() => setBulkRows([...bulkRows, { name: '', date: '' }])}
                                                className='btn btn-outline-secondary text-sm flex items-center gap-5'
                                            >
                                                <IconPlus size={14} /> Add Row
                                            </button>
                                        </div>
                                        <div className='flex gap-10 justify-end mt-20'>
                                            <button type='button' onClick={closeModal} className='btn btn-outline-secondary'>Cancel</button>
                                            <button type='submit' className='btn btn-primary' disabled={createHolidays.isPending}>
                                                {createHolidays.isPending ? 'Saving...' : `Save ${bulkRows.filter(r => r.name && r.date).length} Holiday(s)`}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* Tab 2: Date Range */}
                                {activeTab === 2 && (
                                    <form onSubmit={handleRangeSubmit}>
                                        <div className='grid grid-cols-12 gap-15 mt-10'>
                                            <div className='form-group col-span-12'>
                                                <label className='text-sm mb-2 block'>Holiday Name *</label>
                                                <input
                                                    type='text'
                                                    className='w-full'
                                                    value={rangeForm.name}
                                                    onChange={e => setRangeForm({ ...rangeForm, name: e.target.value })}
                                                    placeholder='e.g. Christmas Break'
                                                />
                                            </div>
                                            <div className='form-group col-span-6'>
                                                <label className='text-sm mb-2 block'>Start Date *</label>
                                                <input
                                                    type='date'
                                                    className='w-full'
                                                    value={rangeForm.start_date}
                                                    onChange={e => setRangeForm({ ...rangeForm, start_date: e.target.value })}
                                                />
                                            </div>
                                            <div className='form-group col-span-6'>
                                                <label className='text-sm mb-2 block'>End Date *</label>
                                                <input
                                                    type='date'
                                                    className='w-full'
                                                    value={rangeForm.end_date}
                                                    onChange={e => setRangeForm({ ...rangeForm, end_date: e.target.value })}
                                                />
                                            </div>
                                            <div className='form-group col-span-12 flex items-center gap-8'>
                                                <input
                                                    type='checkbox'
                                                    id='skipWeekends'
                                                    checked={rangeForm.skip_weekends}
                                                    onChange={e => setRangeForm({ ...rangeForm, skip_weekends: e.target.checked })}
                                                />
                                                <label htmlFor='skipWeekends' className='text-sm cursor-pointer'>
                                                    Skip weekends (Sat &amp; Sun)
                                                </label>
                                            </div>
                                        </div>
                                        <div className='flex gap-10 justify-end mt-20'>
                                            <button type='button' onClick={closeModal} className='btn btn-outline-secondary'>Cancel</button>
                                            <button type='submit' className='btn btn-primary' disabled={createHolidayRange.isPending}>
                                                {createHolidayRange.isPending ? 'Saving...' : 'Save Range'}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                    <div onClick={closeModal} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
                </>
            )}

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null) }}
                onConfirm={confirmDelete}
                title="Delete Holiday"
                message="This holiday will be removed from all employee timesheets."
                itemName={itemToDelete?.name}
                isLoading={deleteHoliday.isPending}
            />
        </>
    )
}
